import {
  ORDERED_ARCHES,
  type MaterialCharacteristicsWithoutShade,
  type TeethArch,
} from "@jasper/client-models";
import { useTranslation } from "react-i18next";
import type { FieldValueProps } from "../../types";
import { useCallback, useMemo } from "react";
import { Group, SegmentedControl, Stack, Text } from "@mantine/core";
import { ButtonSelect, CardSelect } from "../../components";
import { handleGroupBlur } from "../../utils/select";
import {
  isSplintItemType,
  SplintItemTypeEnum,
  type SplintItemType,
} from "./splintItemType";

export interface SplintCharacteristics {
  type: SplintItemType;
  materialCharacteristics?: MaterialCharacteristicsWithoutShade;
  isMaterialPerArch?: boolean;
  materialCharacteristicsByArch?: Partial<
    Record<TeethArch, MaterialCharacteristicsWithoutShade>
  >;
}

const SAME_MATERIAL = "same";
const MATERIAL_PER_ARCH = "perArch";

interface Props extends FieldValueProps<SplintCharacteristics> {
  itemMaterials: { value: string; label: string; itemType: SplintItemType }[];
  arches?: TeethArch[];
}

export const SplintTypeForm = ({
  value,
  errors,
  onChange,
  onBlur,
  itemMaterials,
  arches = [],
}: Props) => {
  const { t } = useTranslation("forms", { keyPrefix: "forms.splintType" });

  const orderedArches = useMemo(
    () => ORDERED_ARCHES.filter(arch => arches.includes(arch)),
    [arches]
  );
  const canDifferPerArch = orderedArches.length > 1;
  const isMaterialPerArch = canDifferPerArch && !!value?.isMaterialPerArch;

  const isItemWithMaterial = useMemo(() => {
    return itemMaterials.some(item => item.itemType === value?.type);
  }, [itemMaterials, value?.type]);

  const itemMatchMaterials = useCallback(
    (itemType: SplintItemType, materialId?: string) => {
      return itemMaterials.some(
        item => item.itemType === itemType && item.value === materialId
      );
    },
    [itemMaterials]
  );

  const materialData = itemMaterials
    .filter(item => item.itemType === value?.type)
    .map(item => ({
      value: item.value,
      label: item.label,
    }));

  const handleTypeChange = (type?: string) => {
    if (!type) {
      return;
    }

    if (!isSplintItemType(type)) {
      return;
    }

    // Keep only the materials the new type offers.
    const keepMaterial = (
      materialCharacteristics?: DeepPartialMaterial
    ): MaterialCharacteristicsWithoutShade | undefined =>
      materialCharacteristics?.material &&
      itemMatchMaterials(type, materialCharacteristics.material)
        ? { material: materialCharacteristics.material }
        : undefined;

    onChange({
      ...value,
      type,
      materialCharacteristics: keepMaterial(value?.materialCharacteristics),
      materialCharacteristicsByArch: Object.fromEntries(
        orderedArches.map(arch => [
          arch,
          keepMaterial(value?.materialCharacteristicsByArch?.[arch]),
        ])
      ),
    });
  };

  const handleModeChange = (mode: string) => {
    if (mode === MATERIAL_PER_ARCH) {
      onChange({
        ...value,
        isMaterialPerArch: true,
        materialCharacteristicsByArch: Object.fromEntries(
          orderedArches.map(arch => [
            arch,
            value?.materialCharacteristics?.material
              ? { material: value.materialCharacteristics.material }
              : undefined,
          ])
        ),
      });
      return;
    }

    const firstArchMaterial = orderedArches
      .map(arch => value?.materialCharacteristicsByArch?.[arch]?.material)
      .find(Boolean);

    onChange({
      ...value,
      isMaterialPerArch: false,
      materialCharacteristics: firstArchMaterial
        ? { material: firstArchMaterial }
        : value?.materialCharacteristics,
    });
  };

  const hardnessError = (hasError: unknown) =>
    hasError ? t("errors.hardness") : undefined;

  return (
    <Stack onBlur={handleGroupBlur(onBlur)}>
      <CardSelect
        value={value?.type}
        data={Object.values(SplintItemTypeEnum).map(type => ({
          value: type,
          label: t(`types.${type}.title`),
          description: t(`types.${type}.description`),
        }))}
        onChange={handleTypeChange}
        error={errors?.type ? t(`errors.type`) : undefined}
      />
      {isItemWithMaterial && (
        <Stack gap="xs">
          <Group justify="space-between" wrap="wrap">
            <Text size="sm" variant="strong">
              {t("labels.hardness")}
            </Text>
            {canDifferPerArch && (
              <SegmentedControl
                value={isMaterialPerArch ? MATERIAL_PER_ARCH : SAME_MATERIAL}
                onChange={handleModeChange}
                data={[
                  { value: SAME_MATERIAL, label: t("labels.sameOnAllArches") },
                  { value: MATERIAL_PER_ARCH, label: t("labels.perArch") },
                ]}
              />
            )}
          </Group>
          {isMaterialPerArch ? (
            orderedArches.map(arch => (
              <Group key={arch} gap="md" wrap="nowrap" align="flex-start">
                <Text size="sm" variant="strong" w={110} pt="s2">
                  {t(`labels.arches.${arch}`)}
                </Text>
                <ButtonSelect
                  value={value?.materialCharacteristicsByArch?.[arch]?.material}
                  data={materialData}
                  onChange={material => {
                    onChange({
                      ...value,
                      materialCharacteristicsByArch: {
                        ...value?.materialCharacteristicsByArch,
                        [arch]: { material },
                      },
                    });
                  }}
                  error={hardnessError(
                    errors?.materialCharacteristicsByArch?.[arch]?.material
                  )}
                />
              </Group>
            ))
          ) : (
            <ButtonSelect
              value={value?.materialCharacteristics?.material}
              data={materialData}
              onChange={material => {
                onChange({
                  ...value,
                  materialCharacteristics: {
                    material,
                  },
                });
              }}
              error={hardnessError(errors?.materialCharacteristics?.material)}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};

type DeepPartialMaterial = { material?: string };
